import type {
  GetValiantMintsQuery,
  GetValiantSwappableAssetsQuery,
} from 'src/generated/graphql';

/** A mint id → { symbol, name, chain, decimals, displayDecimals } lookup. */
export type MintMap = Map<
  string,
  NonNullable<GetValiantMintsQuery['valiantMints']>[number]
>;

/** Build a mint-id → mint map from `getValiantMints` (no chain filter). */
export const buildMintMap = (
  mints: GetValiantMintsQuery['valiantMints'] | undefined,
): MintMap => {
  const map: MintMap = new Map();
  for (const m of mints ?? []) {
    map.set(m.id, m);
  }
  return map;
};

export type SwappableAsset = NonNullable<
  GetValiantSwappableAssetsQuery['valiantSwappableAssets']
>[number];

/**
 * Parse a human decimal string (e.g. "0.0125") into an integer base-unit
 * string using the mint's decimals, via BigInt for exactness. Returns null if
 * the input is not a valid non-negative finite decimal string.
 */
export const humanToBaseUnits = (
  human: string,
  decimals: number,
): string | null => {
  const trimmed = human.trim();
  if (!/^\d*(\.\d*)?$/.test(trimmed) || trimmed === '' || trimmed === '.') {
    return null;
  }
  const [intPart, fracPart = ''] = trimmed.split('.');
  const intDigits = intPart === '' ? '0' : intPart;
  const fracPadded = (fracPart + '0'.repeat(decimals)).slice(0, decimals);
  const base = `${intDigits}${fracPadded}`.replace(/^0+(?=\d)/, '');
  return base === '' ? '0' : base;
};

/** Format a base-unit string into a human decimal string at `displayDecimals`. */
export const baseToHuman = (
  base: string,
  decimals: number,
  displayDecimals = 6,
): string => {
  let neg = false;
  let digits = base;
  if (digits.startsWith('-')) {
    neg = true;
    digits = digits.slice(1);
  }
  const padded = digits.padStart(decimals + 1, '0');
  const intPart = padded.slice(0, padded.length - decimals);
  let fracPart = padded.slice(padded.length - decimals);
  if (decimals === 0) {
    fracPart = '';
  }
  const shown = displayDecimals > 0 ? fracPart.slice(0, displayDecimals) : '';
  const trimmedFrac = shown.replace(/0+$/, '');
  const human = trimmedFrac ? `${intPart}.${trimmedFrac}` : intPart;
  return neg ? `-${human}` : human;
};

/** BigInt-safe compare of two non-negative integer base-unit strings. */
export const baseUnitsLte = (a: string, b: string): boolean => {
  try {
    return BigInt(a) <= BigInt(b);
  } catch {
    return false;
  }
};

/** Valiant chain label → Cheddar `Chain` for block explorer links. */
export const valiantChainToScannerChain = (
  valiantChain: string,
): 'BITCOIN' | 'ETHEREUM' | 'SOLANA' | 'BSC' | 'POLYGON' | 'BITCOIN_CASH' | 'LITECOIN' | null => {
  switch (valiantChain) {
    case 'BTC':
      return 'BITCOIN';
    case 'BCH':
      return 'BITCOIN_CASH';
    case 'LTC':
      return 'LITECOIN';
    case 'ETH':
      return 'ETHEREUM';
    case 'BSC':
      return 'BSC';
    case 'POLYGON':
      return 'POLYGON';
    default:
      return null;
  }
};
